import AppBar from "@mui/material/AppBar";
import * as React from "react";
import {
    Button,
    FormControl, Grid, MenuItem,
    Paper, Select, SelectChangeEvent,
    Table, TableBody, TableCell,
    TableContainer,
    TableHead, TablePagination, TableRow, TextField, Toolbar
} from "@mui/material";
import {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import {DateTimeUtils} from "../../utils/DateTimeUtils";
import {ScrapedDataService} from "../../services/ScrapedDataService";
import {strings} from "../../localization/Localization";
import Box from "@mui/material/Box";
import "../../App.css"
import {FullPageLoadingCircle} from "../../components/FullPageLoadingCircle";
import {Routes} from "../../router/Routes";
import {useNavigate, useSearchParams} from "react-router-dom";
import {defaultRowsPerPageOptions} from "../../common/Constants";
import {PaginationPlus} from "../../components/PaginationPlus";
import {StorageService} from "../../storage/StorageService";
import {NumberParser} from "../../utils/NumberParser";
import {ViewsCountPerDayAndArtistDTO} from "../../models/ViewsCountPerDayAndArtistDTO";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {AppBarPage} from "../../components/AppBarPage";
import {PaginatedSelect} from "../../components/PaginatedSelect";
import {Page} from "../../pagination/Page";
import {ChartComponent} from "../../components/ChartComponent";
import {ViewsCount} from "../../models/ViewsCount";
import {ViewsCountPerDayDTO} from "../../models/ViewsCountPerDayDTO";
import {ScrapedViewsByPlatformPerDayDTO} from "../../models/ScrapedViewsByPlatformPerDayDTO";
import {ScrapedViewsByOfferPerDayDTO} from "../../models/ScrapedViewsByOfferPerDayDTO";
import {ScrapedStreamingContextsPerDayAndArtistDTO} from "../../models/ScrapedStreamingContextsPerDayAndArtistDTO";
import {ScrapedStreamsPerCountryPerDayAndArtistDTO} from "../../models/ScrapedStreamsPerCountryPerDayAndArtistDTO";

const transferToInputDateFormat = (date: Date) => {
    return date.toISOString().split("T")[0];
}

const getDateXDaysAgoValue = (days: number) => {
    return (new Date).getTime() - (days * 24 * 60 * 60 * 1000)
}

function ScrapedDataContent() {
    const TODAY = 0;
    const YESTERDAY = 1;
    const LAST_7_DAYS = 7;
    const LAST_14_DAYS = 14;
    const LAST_30_DAYS = 30;
    const LAST_90_DAYS = 90;
    const LAST_12_MONTHS = 365;
    const DATE_RANGE = -1;

    const [searchParams, setSearchParams] = useSearchParams();

    const id = StorageService.getJWTPayload()?.clientId;
    const [scrapedDataTotalPerDay, setScrapedDataTotalPerDay] = useState<ViewsCountPerDayDTO[]>([]);
    const [scrapedDataPaged, setScrapedDataPaged] = useState<ViewsCountPerDayAndArtistDTO[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalElements, setTotalElements] = useState(0);
    const [dateFrom, setDateFrom] = useState<string>(transferToInputDateFormat(new Date(getDateXDaysAgoValue(LAST_7_DAYS))));
    const [dateTo, setDateTo] = useState<string>(transferToInputDateFormat(new Date()));
    const [artist, setArtist] = useState<string | undefined>(undefined);
    const [periodFilter, setPeriodFilter] = useState<number>(DATE_RANGE);
    const [platformData, setPlatformData] = useState<ScrapedViewsByPlatformPerDayDTO[]>([]);
    const [offerData, setOfferData] = useState<ScrapedViewsByOfferPerDayDTO[]>([]);
    const [contextsDataPaged, setContextsDataPaged] = useState<ScrapedStreamingContextsPerDayAndArtistDTO[]>([]);
    const [contextsTotalElements, setContextsTotalElements] = useState(0);
    const [countriesDataPaged, setCountriesDataPaged] = useState<ScrapedStreamsPerCountryPerDayAndArtistDTO[]>([]);
    const [countriesTotalElements, setCountriesTotalElements] = useState(0);

    const page = NumberParser.parseNumber(searchParams.get("page"), 0);
    const rowsPerPage = NumberParser.parseNumber(searchParams.get("rowsPerPage"), 10);
    const sort = searchParams.get("sort") ?? "";
    const contextsPage = NumberParser.parseNumber(searchParams.get("contextsPage"), 0);
    const contextsRowsPerPage = NumberParser.parseNumber(searchParams.get("contextsRowsPerPage"), 10);
    const contextsSort = searchParams.get("contextsSort") ?? "";
    const countriesPage = NumberParser.parseNumber(searchParams.get("countriesPage"), 0);
    const countriesRowsPerPage = NumberParser.parseNumber(searchParams.get("countriesRowsPerPage"), 10);
    const countriesSort = searchParams.get("countriesSort") ?? "";

    const navigate = useNavigate();

    const periodFilterOptionDropdown = [
        {
            label: "Today",
            id: TODAY
        },
        {
            label: "Yesterday",
            id: YESTERDAY
        },
        {
            label: "Last 7 days",
            id: LAST_7_DAYS
        },
        {
            label: "Last 14 days",
            id: LAST_14_DAYS
        },
        {
            label: "Last 30 days",
            id: LAST_30_DAYS
        },
        {
            label: "Last 90 days",
            id: LAST_90_DAYS
        },
        {
            label: "Last 12 months",
            id: LAST_12_MONTHS
        },
        {
            label: "Date range",
            id: DATE_RANGE
        }
    ]

    const handleDashboardClick = () => {
        navigate(Routes.HOME);
    };

    const handleSummaryReportClick = () => {
        navigate(Routes.SUMMARY_REPORT);
    };

    const handleImportedSongsClick = () => {
        navigate(Routes.IMPORTED_SONGS);
    };

    function setSearchParam(key: string, value: string | null | undefined) {
        if (value !== null && value !== undefined && value !== "") {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
        setSearchParams(searchParams);
    }

    function setPage(page: number) {
        setSearchParam("page", page.toString());
    }

    const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDateFrom(event.target.value);
        setPage(0);
    };

    const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDateTo(event.target.value);
        setPage(0);
    };

    const handleArtistChange = (event: string | undefined) => {
        setArtist(event);
        setPage(0);
    };

    function handleChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchParam("rowsPerPage", event.target.value);
        setPage(0);
    }

    function setContextsPage(page: number) {
        setSearchParam("contextsPage", page.toString());
    }

    function handleContextsChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        setContextsPage(newPage);
    }

    function handleContextsChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchParam("contextsRowsPerPage", event.target.value);
        setContextsPage(0);
    }

    function setCountriesPage(page: number) {
        setSearchParam("countriesPage", page.toString());
    }

    function handleCountriesChangePage(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        setCountriesPage(newPage);
    }

    function handleCountriesChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchParam("countriesRowsPerPage", event.target.value);
        setCountriesPage(0);
    }

    const handlePeriodFilter = (event: SelectChangeEvent) => {
        let value = +event.target.value;

        if (value !== -1) {
            let dateT = new Date();
            let dateF = new Date(getDateXDaysAgoValue(value));

            setDateFrom(transferToInputDateFormat(dateF));
            setDateTo(transferToInputDateFormat(dateT));
        }

        setPage(0);
        setPeriodFilter(value);
    }


    const handleSort = (field: string) => {
        const currentSort = searchParams.get("sort") ?? "";
        let newSort = `${field},asc`;

        if (currentSort.startsWith(`${field},asc`)) {
            newSort = `${field},desc`;
        } else if (currentSort.startsWith(`${field},desc`)) {
            newSort = '';
        }

        if (field === "probability") {
            searchParams.delete("probability");
        }

        if (newSort) {
            searchParams.set("sort", newSort);
        } else {
            searchParams.delete("sort");
        }
        setSearchParams(searchParams);
    };

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);

            const scrapedDataTotalPerDay = await ScrapedDataService.getScrapedViewsCountPerDayWithArtistFilter(
                id,
                DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                artist
            );

            setScrapedDataTotalPerDay(scrapedDataTotalPerDay);
            setIsLoading(false);
        }

        fetchData();
    }, [periodFilter, dateFrom, dateTo, artist]);

    useEffect(() => {
        async function fetchData() {
            let loadingTimeout: NodeJS.Timeout | null = null;

            loadingTimeout = setTimeout(() => {
                setIsLoading(true);
            }, 300);

            try {
                const result = await ScrapedDataService.getScrapedViewsAndTotalListenersCountPerDayPerArtistPaged(
                    id,
                    DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                    DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                    page,
                    rowsPerPage,
                    sort,
                    artist
                );

                setScrapedDataPaged(result.content);
                setTotalElements(result.totalElements);
            } finally {
                if (loadingTimeout) {
                    clearTimeout(loadingTimeout);
                }
                setIsLoading(false);
            }
        }

        fetchData();
    }, [periodFilter, dateFrom, dateTo, page, rowsPerPage, sort, artist]);

    useEffect(() => {
        async function fetchStreamingContexts() {
            const result = await ScrapedDataService.getScrapedStreamingContextsPerDayPerArtistPaged(
                id,
                DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                contextsPage,
                contextsRowsPerPage,
                contextsSort,
                artist ? [artist] : undefined
            );

            setContextsDataPaged(result.content);
            setContextsTotalElements(result.totalElements);
        }

        fetchStreamingContexts();
    }, [periodFilter, dateFrom, dateTo, contextsPage, contextsRowsPerPage, contextsSort, artist]);

    useEffect(() => {
        async function fetchStreamsPerCountry() {
            const result = await ScrapedDataService.getScrapedStreamsPerCountryPerDayPerArtistPaged(
                id,
                DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                countriesPage,
                countriesRowsPerPage,
                countriesSort,
                artist ? [artist] : undefined
            );

            setCountriesDataPaged(result.content);
            setCountriesTotalElements(result.totalElements);
        }

        fetchStreamsPerCountry();
    }, [periodFilter, dateFrom, dateTo, countriesPage, countriesRowsPerPage, countriesSort, artist]);

    useEffect(() => {
        async function fetchData() {
            const result = await ScrapedDataService.getScrapedViewsByPlatformPerDay(
                id,
                DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                artist
            );
            setPlatformData(result);
        }
        fetchData();
    }, [periodFilter, dateFrom, dateTo, artist]);

    useEffect(() => {
        async function fetchData() {
            const result = await ScrapedDataService.getScrapedViewsByOfferPerDay(
                id,
                DateTimeUtils.formatDateDayJs(dayjs(dateFrom)),
                DateTimeUtils.formatDateDayJs(dayjs(dateTo)),
                artist
            );
            setOfferData(result);
        }
        fetchData();
    }, [periodFilter, dateFrom, dateTo, artist]);

    const getSortIcon = (field: string) => {
        const currentSort = searchParams.get("sort") ?? "";
        if (currentSort.startsWith(`${field},asc`)) {
            return <ArrowUpwardIcon fontSize="small" />;
        } else if (currentSort.startsWith(`${field},desc`)) {
            return <ArrowDownwardIcon fontSize="small" />;
        }
        return null;
    };

    const handleContextsSort = (field: string) => {
        const currentSort = searchParams.get("contextsSort") ?? "";
        let newSort = `${field},asc`;

        if (currentSort.startsWith(`${field},asc`)) {
            newSort = `${field},desc`;
        } else if (currentSort.startsWith(`${field},desc`)) {
            newSort = '';
        }

        setSearchParam("contextsSort", newSort);
    };

    const getContextsSortIcon = (field: string) => {
        const currentSort = searchParams.get("contextsSort") ?? "";
        if (currentSort.startsWith(`${field},asc`)) {
            return <ArrowUpwardIcon fontSize="small" />;
        } else if (currentSort.startsWith(`${field},desc`)) {
            return <ArrowDownwardIcon fontSize="small" />;
        }
        return null;
    };

    const handleCountriesSort = (field: string) => {
        const currentSort = searchParams.get("countriesSort") ?? "";
        let newSort = `${field},asc`;

        if (currentSort.startsWith(`${field},asc`)) {
            newSort = `${field},desc`;
        } else if (currentSort.startsWith(`${field},desc`)) {
            newSort = '';
        }

        setSearchParam("countriesSort", newSort);
    };

    const getCountriesSortIcon = (field: string) => {
        const currentSort = searchParams.get("countriesSort") ?? "";
        if (currentSort.startsWith(`${field},asc`)) {
            return <ArrowUpwardIcon fontSize="small" />;
        } else if (currentSort.startsWith(`${field},desc`)) {
            return <ArrowDownwardIcon fontSize="small" />;
        }
        return null;
    };

    const countryColumns: {
        sortField: string;
        label: string;
        value: (item: ScrapedStreamsPerCountryPerDayAndArtistDTO) => string | number;
    }[] = [
        { sortField: 'dateFrom', label: strings.dateColumn, value: item => item.date.split('T')[0] },
        { sortField: 'artist', label: strings.artist, value: item => item.artist },
        { sortField: 'countryName', label: strings.country, value: item => item.countryName },
        { sortField: 'streams', label: strings.streamsBot, value: item => item.streams },
    ];

    const formatContextCell =(total?: number | null, percent?: number | null) => {
        if (total === null || total === undefined) {
            return '-';
        }

        if (percent === null || percent === undefined) {
            return `${total}`;
        }

        return `${total} (${percent.toFixed(2)}%)`;
    };

    const contextColumns: {
        sortField: string;
        label: string;
        total: (item: ScrapedStreamingContextsPerDayAndArtistDTO) => number;
        percent: (item: ScrapedStreamingContextsPerDayAndArtistDTO) => number;
    }[] = [
        { sortField: 'playlistTotal', label: strings.contextPlaylist, total: item => item.playlistTotal, percent: item => item.playlistPercent },
        { sortField: 'albumTotal', label: strings.contextAlbum, total: item => item.albumTotal, percent: item => item.albumPercent },
        { sortField: 'artistPageTotal', label: strings.contextArtistPage, total: item => item.artistPageTotal, percent: item => item.artistPagePercent },
        { sortField: 'lovedTracksTotal', label: strings.contextLovedTracks, total: item => item.lovedTracksTotal, percent: item => item.lovedTracksPercent },
        { sortField: 'mixFlowTotal', label: strings.contextMixFlow, total: item => item.mixFlowTotal, percent: item => item.mixFlowPercent },
        { sortField: 'otherTotal', label: strings.contextOther, total: item => item.otherTotal, percent: item => item.otherPercent },
    ];

    function aggregateDataByDay() {
        const dataMap = new Map<string, ViewsCountPerDayDTO>();
        scrapedDataTotalPerDay.forEach(item => {
            const dateString = item.date.split('T')[0];
            dataMap.set(dateString, item);
        });

        const startDate = dayjs(dateFrom);
        const endDate = dayjs(dateTo);
        const viewCounts: number[] = [];
        const uniqueListenersCounts: number[] = [];

        let current = startDate;
        while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
            const dateString = current.format('YYYY-MM-DD');
            const data = dataMap.get(dateString);
            viewCounts.push(data?.viewCount ?? 0);
            uniqueListenersCounts.push(data?.uniqueListeners ?? 0);
            current = current.add(1, 'day');
        }

        return [
            {
                name: strings.streamsBot,
                data: viewCounts
            },
            {
                name: strings.uniqueListeners,
                data: uniqueListenersCounts
            }
        ];
    }

    function aggregatePlatformDataByDay() {
        const dataMap = new Map<string, { mobileViews: number; webViews: number }>();

        platformData.forEach(item => {
            const dateString = item.date.split('T')[0];
            const existing = dataMap.get(dateString);
            if (existing) {
                existing.mobileViews += item.mobileViews;
                existing.webViews += item.webViews;
            } else {
                dataMap.set(dateString, {
                    mobileViews: item.mobileViews,
                    webViews: item.webViews
                });
            }
        });

        const startDate = dayjs(dateFrom);
        const endDate = dayjs(dateTo);
        const mobileCounts: number[] = [];
        const webCounts: number[] = [];

        let current = startDate;
        while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
            const dateString = current.format('YYYY-MM-DD');
            const data = dataMap.get(dateString);
            mobileCounts.push(data?.mobileViews ?? 0);
            webCounts.push(data?.webViews ?? 0);
            current = current.add(1, 'day');
        }

        return [
            { name: strings.mobileViews, data: mobileCounts },
            { name: strings.browserViews,    data: webCounts }
        ];
    }

    function aggregateOfferDataByDay() {
        const dataMap = new Map<string, { adSupportedViews: number; subscriberViews: number }>();

        offerData.forEach(item => {
            const dateString = item.date.split('T')[0];
            const existing = dataMap.get(dateString);
            if (existing) {
                existing.adSupportedViews += item.adSupportedViews;
                existing.subscriberViews += item.subscriberViews;
            } else {
                dataMap.set(dateString, {
                    adSupportedViews: item.adSupportedViews,
                    subscriberViews: item.subscriberViews
                });
            }
        });

        const startDate = dayjs(dateFrom);
        const endDate = dayjs(dateTo);
        const adSupportedCounts: number[] = [];
        const subscriberCounts: number[] = [];

        let current = startDate;
        while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
            const dateString = current.format('YYYY-MM-DD');
            const data = dataMap.get(dateString);
            adSupportedCounts.push(data?.adSupportedViews ?? 0);
            subscriberCounts.push(data?.subscriberViews ?? 0);
            current = current.add(1, 'day');
        }

        return [
            { name: strings.adSupportedViews, data: adSupportedCounts },
            { name: strings.subscriberViews,  data: subscriberCounts }
        ];
    }

    function getDateCategories() {
        const startDate = dayjs(dateFrom);
        const endDate = dayjs(dateTo);
        const categories: string[] = [];

        let current = startDate;
        while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
            categories.push(current.format('YYYY-MM-DD'));
            current = current.add(1, 'day');
        }

        return categories;
    }

    const tickAmountChange = (dateFrom?: string, dateTo?: string) => {
        if (!dateFrom || !dateTo) return 0;

        const daysDifference = dayjs(dateTo).diff(dayjs(dateFrom), 'day');
        return daysDifference <= 30 ? daysDifference + 1 : 29;
    };

    return (
        <>
            <FullPageLoadingCircle loading={isLoading} />

            <AppBar
                position="absolute"
                sx={{ marginTop: "65.5px", minHeight: "65.5px" }}
                color="secondary"
            >
                <Toolbar>
                    <Button
                        onClick={handleDashboardClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.dashboard}
                    </Button>
                    <Button
                        onClick={handleSummaryReportClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.summaryReport}
                    </Button>
                    <Button
                        onClick={handleImportedSongsClick}
                        sx={{
                            marginLeft: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            color: "black",
                            backgroundColor: "#efefef",
                            width: "10%",
                            '&:hover': {
                                backgroundColor: "lightgray",
                            },
                        }}
                    >
                        {strings.importedSongs}
                    </Button>
                </Toolbar>
            </AppBar>

            <Box>
                <Typography style={{
                    fontSize: "2rem",
                    fontWeight: "bold",
                    marginTop: "108px",
                    marginBottom: "70px"
                }}>{strings.scrapedDataHeader}</Typography>

                <div className={"mainDiv"} style={{
                    flexDirection: "row",
                    marginLeft: "40px",
                    width: "77.5%",
                    justifyContent: "space-between",
                    marginTop: "25px",
                    marginBottom: "25px"
                }}>
                    <div style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flex: "1 1 calc(33.333% - 20px)",
                        minWidth: "300px",
                        maxWidth: "400px"
                    }}>
                        <FormControl variant="outlined" fullWidth>
                            <PaginatedSelect<string>
                                value={artist}
                                onChange={handleArtistChange}
                                label={strings.artist}
                                valueMapper={(item) => item.toString()}
                                keyMapper={(item) => item.toString()}
                                itemMapper={(item) => <>{item}</>}
                                labelMapper={(item) => item}
                                dataFetcher={(page, size, filter) => {
                                    if (id) {
                                        return ScrapedDataService.getAllArtistsFromScrapedData(
                                            page,
                                            size,
                                            id,
                                            filter
                                        );
                                    } else {
                                        return Promise.resolve(new Page<string>([], 0));
                                    }
                                }}
                                inputProps={{
                                    sx: {
                                        color: "black",
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'black',
                                            borderWidth: '1.5px',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'black',
                                            borderWidth: '1.5px',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'black',
                                            borderWidth: "2px"
                                        },
                                        '.MuiSvgIcon-root': {
                                            fill: "black !important",
                                        },
                                        backgroundColor: "white",
                                        borderRadius: '1px'
                                    }
                                }}
                            />
                        </FormControl>
                    </div>

                    <div style={{display: "flex", flexDirection: "row"}}>
                        <FormControl className="formControl" variant="outlined"
                                     style={{float: "right", display: "flex", flexDirection: "row"}} fullWidth>

                            <Typography style={{
                                marginRight: "20px",
                                fontSize: "18px",
                                marginTop: "15px"
                            }}>{strings.period}</Typography>
                            <Select className="dropdown-menu3"
                                    value={periodFilter.toString()}
                                    onChange={handlePeriodFilter}
                                    style={{
                                        border: "black",
                                        borderStyle: "solid",
                                        borderWidth: "1.5px",
                                        borderRadius: "1px",
                                        backgroundColor: "White"
                                    }}
                            >
                                {periodFilterOptionDropdown?.map((period) => (
                                    <MenuItem disableRipple key={period.id} value={period.id}>
                                        {period.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                </div>
                {periodFilter === -1 &&
                    <div className={"mainDiv"} style={{
                        flexDirection: "row",
                        marginLeft: "40px",
                        width: "77.5%",
                        justifyContent: "flex-end",
                        marginBottom: "25px"
                    }}>
                        <div className="div1" style={{display: "flex", flexDirection: "row"}}>

                            <FormControl className="formControl" variant="outlined"
                                         style={{float: "right", display: "flex", flexDirection: "row"}}
                                         fullWidth>
                                <Typography style={{
                                    marginRight: "20px",
                                    fontSize: "18px",
                                    marginTop: "15px"
                                }}>{strings.dateFrom}</Typography>
                                <TextField
                                    type="date"
                                    value={dateFrom}
                                    onChange={handleDateFromChange}
                                    InputLabelProps={{shrink: true}}
                                    style={{
                                        border: "black",
                                        borderStyle: "solid",
                                        borderWidth: "1.5px",
                                        borderRadius: "1px",
                                        backgroundColor: "White"
                                    }}
                                />
                            </FormControl>
                        </div>

                        <div className={"div2"}
                             style={{display: "flex", flexDirection: "row", marginLeft: "20px"}}>
                            <FormControl className="formControl" variant="outlined"
                                         style={{float: "right", display: "flex", flexDirection: "row"}}
                                         fullWidth>
                                <Typography style={{
                                    marginRight: "20px",
                                    fontSize: "18px",
                                    marginTop: "15px"
                                }}>{strings.dateTo}</Typography>
                                <TextField
                                    type="date"
                                    value={dateTo}
                                    onChange={handleDateToChange}
                                    InputLabelProps={{shrink: true}}
                                    style={{
                                        border: "black",
                                        borderStyle: "solid",
                                        borderWidth: "1.5px",
                                        borderRadius: "1px",
                                        backgroundColor: "White"
                                    }}
                                />
                            </FormControl>
                        </div>
                    </div>
                }
                <Box sx={{ marginTop: "100px" }}>
                    <ChartComponent
                        series={aggregateDataByDay()}
                        views={scrapedDataTotalPerDay.reduce((sum, item) => sum + item.viewCount, 0)}
                        streamsFilter={ViewsCount.STAT_STREAMS}
                        text={artist ? `${strings.totalForArtist} ${artist}` : strings.totalForEveryArtist}
                        categories={getDateCategories()}
                        tickAmount={tickAmountChange()}
                        xaxisType="datetime"
                    />
                </Box>
                <Box sx={{ marginTop: "60px" }}>
                    <ChartComponent
                        series={aggregatePlatformDataByDay()}
                        views={platformData.reduce((sum, item) => sum + item.mobileViews + item.webViews, 0)}
                        streamsFilter={ViewsCount.STAT_STREAMS}
                        text={artist ? `${strings.totalForArtistByPlatform} ${artist}` : strings.totalForEveryArtistByPlatform}
                        categories={getDateCategories()}
                        tickAmount={tickAmountChange()}
                        xaxisType="datetime"
                    />
                </Box>
                <Box sx={{ marginTop: "60px" }}>
                    <ChartComponent
                        series={aggregateOfferDataByDay()}
                        views={offerData.reduce((sum, item) => sum + item.adSupportedViews + item.subscriberViews, 0)}
                        streamsFilter={ViewsCount.STAT_STREAMS}
                        text={artist ? `${strings.totalForArtistByOffer} ${artist}` : strings.totalForEveryArtistByOffer}
                        categories={getDateCategories()}
                        tickAmount={tickAmountChange()}
                        xaxisType="datetime"
                    />
                </Box>
                    <TableContainer
                        component={Paper}
                        style={{marginTop: "70px", borderRadius: "10px", marginBottom: "70px"}}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        onClick={() => handleSort('dateFrom')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.dateColumn} {getSortIcon('dateFrom')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('artist')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.artist} {getSortIcon('artist')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('viewCount')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.streamsBot} {getSortIcon('viewCount')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('uniqueListeners')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.uniqueListeners} {getSortIcon('uniqueListeners')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('mobileViews')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.mobileViews} {getSortIcon('mobileViews')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('webViews')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.browserViews} {getSortIcon('webViews')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('adSupportedViews')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.adSupportedViews} {getSortIcon('adSupportedViews')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('subscriberViews')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.subscriberViews} {getSortIcon('subscriberViews')}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scrapedDataPaged.map((item: any) => (
                                    <TableRow key={item.id} hover={true}>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.date.split('T')[0]}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.artist}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.viewCount}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.uniqueListeners}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.mobileViews ?? '-'}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.webViews ?? '-'}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.adSupportedViews ?? '-'}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.subscriberViews ?? '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Grid sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'end',
                            marginTop: "10px"
                        }}>
                            <PaginationPlus
                                totalElements={totalElements}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onChangePage={(page) => handleChangePage(null, page)}
                            />
                            <TablePagination
                                rowsPerPageOptions={defaultRowsPerPageOptions}
                                component="div"
                                count={totalElements}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Grid>
                    </TableContainer>
                    <Typography variant="h5" sx={{marginTop: "70px", marginBottom: "30px", fontWeight: "bold"}}>
                        {strings.streamingContextsTable}
                    </Typography>
                    <TableContainer
                        component={Paper}
                        style={{borderRadius: "10px", marginBottom: "70px"}}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        onClick={() => handleContextsSort('dateFrom')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.dateColumn} {getContextsSortIcon('dateFrom')}
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleContextsSort('artist')}
                                        style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                    >
                                        <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            {strings.artist} {getContextsSortIcon('artist')}
                                        </div>
                                    </TableCell>
                                    {contextColumns.map((column) => (
                                        <TableCell
                                            key={column.sortField}
                                            onClick={() => handleContextsSort(column.sortField)}
                                            style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                        >
                                            <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                                {column.label} {getContextsSortIcon(column.sortField)}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contextsDataPaged.map((item: ScrapedStreamingContextsPerDayAndArtistDTO, index: number) => (
                                    <TableRow key={`${item.date}-${item.artist}-${index}`} hover={true}>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.date.split('T')[0]}</TableCell>
                                        <TableCell style={{fontFamily: "sans-serif"}}>{item.artist}</TableCell>
                                        {contextColumns.map((column) => (
                                            <TableCell key={column.sortField} style={{fontFamily: "sans-serif"}}>
                                                {formatContextCell(column.total(item), column.percent(item))}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Grid sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'end',
                            marginTop: "10px"
                        }}>
                            <PaginationPlus
                                totalElements={contextsTotalElements}
                                rowsPerPage={contextsRowsPerPage}
                                page={contextsPage}
                                onChangePage={(page) => handleContextsChangePage(null, page)}
                            />
                            <TablePagination
                                rowsPerPageOptions={defaultRowsPerPageOptions}
                                component="div"
                                count={contextsTotalElements}
                                rowsPerPage={contextsRowsPerPage}
                                page={contextsPage}
                                onPageChange={handleContextsChangePage}
                                onRowsPerPageChange={handleContextsChangeRowsPerPage}
                            />
                        </Grid>
                    </TableContainer>
                    <Typography variant="h5" sx={{marginTop: "70px", marginBottom: "30px", fontWeight: "bold"}}>
                        {strings.streamsPerCountryTable}
                    </Typography>
                    <TableContainer
                        component={Paper}
                        style={{borderRadius: "10px", marginBottom: "70px"}}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {countryColumns.map((column) => (
                                        <TableCell
                                            key={column.sortField}
                                            onClick={() => handleCountriesSort(column.sortField)}
                                            style={{fontFamily: "sans-serif", cursor: "pointer", userSelect: "none"}}
                                        >
                                            <div style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                                {column.label} {getCountriesSortIcon(column.sortField)}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {countriesDataPaged.map((item: ScrapedStreamsPerCountryPerDayAndArtistDTO, index: number) => (
                                    <TableRow key={`${item.date}-${item.artist}-${item.countryCode}-${index}`} hover={true}>
                                        {countryColumns.map((column) => (
                                            <TableCell key={column.sortField} style={{fontFamily: "sans-serif"}}>
                                                {column.value(item)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Grid sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'end',
                            marginTop: "10px"
                        }}>
                            <PaginationPlus
                                totalElements={countriesTotalElements}
                                rowsPerPage={countriesRowsPerPage}
                                page={countriesPage}
                                onChangePage={(page) => handleCountriesChangePage(null, page)}
                            />
                            <TablePagination
                                rowsPerPageOptions={defaultRowsPerPageOptions}
                                component="div"
                                count={countriesTotalElements}
                                rowsPerPage={countriesRowsPerPage}
                                page={countriesPage}
                                onPageChange={handleCountriesChangePage}
                                onRowsPerPageChange={handleCountriesChangeRowsPerPage}
                            />
                        </Grid>
                    </TableContainer>
            </Box>
        </>
);
}

export function ScrapedDataPage() {
    return (
        <AppBarPage
            pageTitle={strings.analytics}
            component={<ScrapedDataContent/>}
        />
    );
}
